import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, Radar
} from 'recharts';
import { 
  TrendingUp, Target, Trophy, Zap, 
  Activity, PieChartIcon, BarChart3 
} from 'lucide-react';

const SPORT_COLORS: Record<string, string> = {
  'Football': '#22c55e',
  'Basketball': '#f97316',
  'Tennis': '#eab308',
  'Esports': '#a855f7',
};

const CHART_COLORS = ['#22c55e', '#f97316', '#eab308', '#a855f7', '#06b6d4'];

export function CrossSportAnalytics() {
  const { data: sportAnalytics, isLoading } = useQuery({
    queryKey: ['sport-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sport_analytics')
        .select('*')
        .eq('period', 'weekly')
        .order('total_tips', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const pieData = sportAnalytics?.map(s => ({
    name: s.sport,
    value: s.total_tips,
    color: SPORT_COLORS[s.sport] || '#888',
  }));

  const roiData = sportAnalytics?.map(s => ({
    sport: s.sport,
    roi: s.roi_percentage,
    winRate: (s.winning_tips / s.total_tips * 100).toFixed(1),
  }));

  const radarData = sportAnalytics?.map(s => ({
    sport: s.sport,
    'Win Rate': (s.winning_tips / s.total_tips * 100),
    'ROI': s.roi_percentage,
    'Confidence': s.avg_confidence,
    'Volume': Math.min((s.total_tips / 15), 100),
  }));

  return (
    <div className="space-y-6">
      <Card className="glass-card overflow-hidden">
        <div className="bg-gradient-to-r from-green-600/20 via-blue-500/20 to-purple-500/20 p-6">
          <div className="flex items-center gap-3">
            <Activity className="h-8 w-8 text-primary" />
            <div>
              <h2 className="text-2xl font-bold">Cross-Sport Analytics</h2>
              <p className="text-muted-foreground">
                Compare performance across all sports
              </p>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {sportAnalytics?.map((sport) => (
          <Card key={sport.sport} className="glass-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{sport.sport}</span>
                <Badge 
                  variant="outline" 
                  style={{ borderColor: SPORT_COLORS[sport.sport], color: SPORT_COLORS[sport.sport] }}
                >
                  {sport.roi_percentage}% ROI
                </Badge>
              </div>
              <p className="text-2xl font-bold">{sport.total_tips}</p>
              <p className="text-xs text-muted-foreground">tips this week</p>
              <Progress 
                value={(sport.winning_tips / sport.total_tips) * 100} 
                className="mt-2 h-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {((sport.winning_tips / sport.total_tips) * 100).toFixed(0)}% win rate
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="comparison" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 h-auto">
          <TabsTrigger value="comparison" className="py-2">
            <BarChart3 className="h-4 w-4 mr-2" />
            ROI Comparison
          </TabsTrigger>
          <TabsTrigger value="distribution" className="py-2">
            <PieChartIcon className="h-4 w-4 mr-2" />
            Tip Distribution
          </TabsTrigger>
          <TabsTrigger value="radar" className="py-2">
            <Target className="h-4 w-4 mr-2" />
            Performance Radar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="comparison">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>ROI by Sport</CardTitle>
              <CardDescription>Weekly return on investment comparison</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={roiData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="sport" stroke="#888" />
                    <YAxis stroke="#888" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="roi" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="distribution">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Tip Volume Distribution</CardTitle>
              <CardDescription>Tips across different sports this week</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80 flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      innerRadius={60}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {pieData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-4">
                {pieData?.map((entry) => (
                  <div key={entry.name} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-sm">{entry.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="radar">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Performance Radar</CardTitle>
              <CardDescription>Multi-dimensional sport comparison</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#333" />
                    <PolarAngleAxis dataKey="sport" stroke="#888" />
                    <Radar
                      name="Win Rate"
                      dataKey="Win Rate"
                      stroke="#22c55e"
                      fill="#22c55e"
                      fillOpacity={0.3}
                    />
                    <Radar
                      name="ROI"
                      dataKey="ROI"
                      stroke="#f97316"
                      fill="#f97316"
                      fillOpacity={0.3}
                    />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Popular Leagues by Sport
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sportAnalytics?.map((sport) => (
              <div key={sport.sport} className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: SPORT_COLORS[sport.sport] }}
                  />
                  <span className="font-medium">{sport.sport}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sport.popular_leagues?.map((league) => (
                    <Badge key={league} variant="secondary">{league}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
